import type { UserData } from "shared-core/types/user.ts";
import { usePostHog } from "./use-posthog";

interface CrispQueueInterface {
  push(command: unknown[]): unknown;
}

const CRISP_IDENTIFIED_KEY = "crisp_user_identified";

let chatOpenedByUser = false;

function crispQueue(): CrispQueueInterface {
  const crispWindow: Window & { $crisp?: CrispQueueInterface } = window;
  crispWindow.$crisp ||= [];
  return crispWindow.$crisp;
}

export function useCrisp(): {
  loadCrisp: (options?: { hideLauncher?: boolean }) => void;
} {
  const loadCrisp = (options: { hideLauncher?: boolean } = {}): void => {
    if (import.meta.env.VITE_DISABLE_CRISP === "true") {
      return;
    }

    if (
      import.meta.env.DEV &&
      (import.meta.env.VITE_FORCE_CRISP === "false" ||
        !import.meta.env.VITE_FORCE_CRISP)
    ) {
      return;
    }

    // @ts-expect-error this is the official way of initiating Crisp
    window.$crisp ||= [];
    // @ts-expect-error this is the official way of initiating Crisp
    window.CRISP_WEBSITE_ID = "4b1e3db4-9f33-45ac-8251-10acc2f921b7";

    (function (): void {
      const d = document;
      const s = d.createElement("script");
      s.src = "https://client.crisp.chat/l.js";
      // @ts-expect-error this is the official way of initiating Crisp
      s.async = 1;
      // Required: COEP require-corp needs CORS mode for cross-origin scripts in WebKit
      s.crossOrigin = "anonymous";
      d.head.appendChild(s);
    })();

    // Force showing widget in agent. On phones the launcher covers the page's bottom actions, so it stays hidden until Help opens the chat.
    const launcherCommand = options.hideLauncher ? "chat:hide" : "chat:show";
    // @ts-expect-error Crisp push API
    window.$crisp.push([
      "on",
      "session:loaded",
      (): void => {
        // Help tapped while Crisp was still loading must not be hidden again.
        if (options.hideLauncher && chatOpenedByUser) {
          return;
        }
        // @ts-expect-error Crisp push API
        window.$crisp.push(["do", launcherCommand]);
      },
    ]);
    if (options.hideLauncher) {
      // @ts-expect-error Crisp push API
      window.$crisp.push([
        "on",
        "chat:closed",
        (): void => {
          chatOpenedByUser = false;
          // @ts-expect-error Crisp push API
          window.$crisp.push(["do", "chat:hide"]);
        },
      ]);
    }

    // Capture conversation started in PostHog
    // https://help.crisp.chat/en/article/how-to-push-crisp-chat-events-to-a-product-analytics-software-like-mixpanel-posthog-or-amplitude-rkap7a/
    // @ts-expect-error Crisp is added to window
    window.$crisp.push([
      "on",
      "message:send",
      async function (): Promise<void> {
        if (sessionStorage.getItem("ph_crisp_conversation_started")) return;

        const { posthog } = await usePostHog("on_reject");
        posthog.capture("crisp_conversation_started", {
          source: "crisp",
          page: location.pathname + location.search,
        });

        sessionStorage.setItem("ph_crisp_conversation_started", "1");
      },
    ]);
  };

  return { loadCrisp };
}

export function openCrispChat(): void {
  // @ts-expect-error $crisp is injected by the Crisp loader script
  if (typeof window.$crisp !== "undefined") {
    chatOpenedByUser = true;
    // @ts-expect-error $crisp is injected by the Crisp loader script
    window.$crisp.push(["do", "chat:show"]);
    // @ts-expect-error $crisp is injected by the Crisp loader script
    window.$crisp.push(["do", "chat:open"]);
  } else {
    console.warn(
      "[Crisp] Chat widget is not loaded. Set VITE_FORCE_CRISP=true in .env to enable it in dev mode.",
    );
  }
}

export function verifyUserInCrisp(user: UserData): void {
  // @ts-expect-error $crisp this is the official way of initiating Crisp
  window.$crisp ||= [];

  if (user.crispSignature) {
    try {
      // @ts-expect-error $crisp exists at this point
      window.$crisp.push([
        "set",
        "user:email",
        [user.email, user.crispSignature],
      ]);
      window.localStorage.setItem(CRISP_IDENTIFIED_KEY, "true");
    } catch (e) {
      console.error("Failed to verify user email for Crisp", e);
    }
  } else {
    console.warn("Missing crispSignature");
  }
}

export function verifyUserInCrispWhenChatOpens(user: UserData): void {
  const crisp = crispQueue();
  crisp.push(["off", "chat:opened"]);
  crisp.push(["on", "chat:opened", (): void => verifyUserInCrisp(user)]);
}

// Crisp keeps its session across app restarts, so the marker has to outlive the signed-in user too.
export function forgetUserInCrisp(): void {
  crispQueue().push(["off", "chat:opened"]);
  if (window.localStorage.getItem(CRISP_IDENTIFIED_KEY) === null) {
    return;
  }
  crispQueue().push(["do", "session:reset"]);
  window.localStorage.removeItem(CRISP_IDENTIFIED_KEY);
}
